import React, { useState, useEffect, useCallback } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import Formulaire from "./pages/Formulaire";
import Demande from "./pages/Demande";
import Header from "./components/Header";
import Home from "./pages/Home";
import Taches from "./pages/Taches";
import { useIsSignedIn } from "@microsoft/mgt-react"; // Hook pour vérifier si l'utilisateur est connecté via Microsoft Graph Toolkit
import { Providers } from "@microsoft/mgt"; // Fournisseur d'authentification Microsoft
import FormulaireAdresse from "./pages/FormulaireAdresse";
import FormulaireNom from "./pages/FormulaireNom";
import GroupRoles from "./pages/GroupRoles";
import FormulaireReseauInfo from "./pages/FormulaireReseauInfo";
import FormulaireInfoBancaires from "./pages/FormulaireInfoBancaires";
import GestionDonneeFinance from "./pages/GestionDonneeFinance";
import ScrollToTop from "./components/ScrollToTop"; // Composant pour revenir en haut à chaque changement de route
import FormAefo from "./pages/FormAefo";
import { apiFetch } from "./utils/apiClient"; // Wrapper fetch personnalisé
import FormFeeso from "./pages/FormFeeso";
import FormScfp from "./pages/FormScfp";
import ArchivesForms from "./pages/ArchivesForms";
import FormAchatEI from "./pages/FormAchatEI";

import { io } from "socket.io-client";
import AntecedentCriminel from "./pages/AntecedentCriminel";
import AccesHorsPays from "./pages/AccesHorsPays";
import DemandeProjet from "./pages/DemandeProjet";
import EntenteTravailHybrid from "./pages/EntenteTravailHybrid";
import ParticipationFormation from "./pages/ParticipationFormation";
import { showError } from "./components/ErrorPopup";

const socket = io.connect(`${process.env.REACT_APP_API_BASE_URL}`); 

const allowedRedirectPaths = new Set([
  "/demande",
  "/taches",
  "/formulaire",
  "/EntenteTravailHybrid",
  "/achatEquipInfo",
  "/participationFormation",
]);

function AuthenticationRedirect() {
  const location = useLocation();

  if (allowedRedirectPaths.has(location.pathname)) {
    localStorage.setItem(
      "redirectAfterLogin",
      `${location.pathname}${location.search}`,
    );
  }

  return <Navigate to="/" replace />;
}

// import { ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";

function App() {

  // Hook pour vérifier si l'utilisateur est connecté
  const [isSignedIn] = useIsSignedIn();
  // État pour stocker les infos de l'utilisateur
  const [user, setUser] = useState(null);
   // État pour gérer le chargement général et le chargement d'authentification
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);

  // **************************************************************** //
  // États pour vérifier les rôles/groupes auxquels appartient l'utilisateur
  const [checkGroupAdresse, setcheckGroupAdresse] = useState(null);
  const [checkGroupAdmin, setcheckGroupAdmin] = useState(null);
  const [checkGroupNom, setcheckGroupNom] = useState(null);
  const [checkGroupReseauInfo, setCheckGroupReseauInfo] = useState(null);
  const [checkGroupInfoBancaire, setcheckGroupInfoBancaire] = useState(null);
  const [checkGroupAntCriminels, setcheckGroupAntCriminels] = useState(null);
  const [checkGroupRecMensuelle, setcheckGroupRecMensuelle] = useState(null);
  const [checkGroupSeancePlacement, setcheckGroupSeancePlacement] = useState(null);
  const [checkGroupTeletravail, setcheckGroupTeletravail] = useState(null);
  const [checkGroupConsultationAchatEI, setCheckGroupConsultationAchatEI] = useState(null);

  // Informations sur le membre personnel
  const [membrePersonnel, setMembrePersonnel] = useState({});
  const [existMembre, setExistMembre] = useState("");
  // Configuration des routes dynamiques
  const [routesConfig, setRoutesConfig] = useState([]);


  // Fonction pour attendre que le provider Microsoft soit prêt
  const waitForProvider = async () => {
    return new Promise((resolve) => {
      const interval = setInterval(() => {
        const provider = Providers.globalProvider;
        if (provider) {
          clearInterval(interval);
          resolve(provider);
        }
      }, 500); // Vérifie toutes les 500ms
    });
  };
  

  // Fonction pour récupérer les données de l'utilisateur depuis Microsoft Graph
  const fetchUser = useCallback(async () => {
    try {
      const provider = await waitForProvider();
      const graphClient = provider.graph.client;
      // const userData = await graphClient.api("/me").get();
      const userData = await graphClient
        .api(
          "/me?$select=displayName,givenName,jobTitle,employeeId,mail,id,officeLocation"
        )
        .get();

      setUser(userData);
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  }, []);


  // Fonction pour vérifier les rôles/groupes de l'utilisateur via l'API backend
  const checkuser = useCallback(async () => {
    // Check if user exists in AdminRolesAdresse
    if (user) {
      
          const response = await apiFetch(
            `${process.env.REACT_APP_API_BASE_URL}/checkUserRole`
          );
          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.error || data.message);
          }
          
          // Mise à jour des états de rôles/groupes
          setcheckGroupAdresse(data.GroupRolesAdresse.exists);
          setcheckGroupAdmin(data.GroupRolesAdmin.exists);
          setcheckGroupNom(data.GroupRolesNom.exists);
          setCheckGroupReseauInfo(data.GroupRolesReseauInf.exists);
          setcheckGroupInfoBancaire(data.GroupRolesInfoBancaire.exists);
          setcheckGroupAntCriminels(data.GroupRolesAntCriminels.exists);
          setcheckGroupRecMensuelle(data.GroupRolesRecMensuelle.exists);
          setcheckGroupSeancePlacement(data.GroupRolesSeancePlacement.exists)
          setcheckGroupTeletravail(data.GroupRolesTeletravail.exists)
          setCheckGroupConsultationAchatEI(data.GroupRolesConsultationAchatEI.exists)
        
    }
  }, [user]);


  // Hook useEffect pour lancer la récupération utilisateur dès la connexion
  useEffect(() => {
    
    if (isSignedIn) {
      fetchUser();
    } else {
      // Si non connecté, on reset tout
      setUser(null);
      setLoading(false);
      setcheckGroupAdresse(null);
      setcheckGroupAdmin(null);
    }
    // Stop le chargement d'authentification après 1s
    setTimeout(() => {
      setAuthLoading(false); 
    }, 1000);
  }, [isSignedIn, fetchUser]);

  // Vérifie les rôles de l'utilisateur dès que ses infos sont disponibles
  useEffect(() => {
    if (user) {
      checkuser();
    }
  }, [user, checkuser]);

  // Récupération dynamique de la configuration des routes depuis l'API
useEffect(() => {
    async function fetchRoutes() {
      try {
        const res = await apiFetch(
          `${process.env.REACT_APP_API_BASE_URL}/routeConfigSchema`
        );
        const data = await res.json();

         // Map chaque route à son composant React correspondant
        const mappedRoutes = data.map((route) => ({
          ...route,
          element: () =>
            getRouteElement(
              // route.path,
              route, // 👈 ON PASSE LA ROUTE
              data,  // 👈 TOUTES LES ROUTES (routesConfig)
              user,
              checkGroupRecMensuelle,
              checkGroupAdmin,
              checkGroupSeancePlacement,
              checkGroupConsultationAchatEI
            ),
        }));
        setRoutesConfig(mappedRoutes);
      } catch (err) {
        console.error("Erreur récupération routes:", err);
      }
    }
    fetchRoutes();
    // 🔔 écouter les mises à jour
  socket.on("routesUpdated", () => {
    fetchRoutes();
  });

  return () => {
    socket.off("routesUpdated");
  };
  }, [user, checkGroupRecMensuelle, checkGroupAdmin, checkGroupSeancePlacement, checkGroupConsultationAchatEI, membrePersonnel]);
  // useEffect(() => {
  //   async function fetchRoutes() {
  //     try {
  //       const res = await apiFetch(
  //         `${process.env.REACT_APP_API_BASE_URL}/routeConfigSchema`
  //       );
  //       const data = await res.json();

  //        // Map chaque route à son composant React correspondant
  //       const mappedRoutes = data.map((route) => ({
  //         ...route,
  //         element: () =>
  //           getRouteElement(
  //             route.path,
  //             user,
  //             checkGroupRecMensuelle,
  //             checkGroupAdmin,
  //             checkGroupSeancePlacement
  //           ),
  //       }));
  //       setRoutesConfig(mappedRoutes);
  //     } catch (err) {
  //       console.error("Erreur récupération routes:", err);
  //     }
  //   }
  //   fetchRoutes();
  // }, [user, checkGroupRecMensuelle, checkGroupAdmin, checkGroupSeancePlacement, membrePersonnel]);


  // Récupération des infos du membre personnel (Séance de placemenet) depuis l'API
  const fetchMembrePersonnel = useCallback(async () => {
    try {
      const res = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/listePersonnel?matricule=${encodeURIComponent(user.employeeId)}`
      );

      const data = await res.json();
      if (!res.ok) {
            throw new Error(data.error || data.message);
          }

      if (data) {
        setMembrePersonnel(data.personnel || {});
        setExistMembre(data.exist || "");
      }

    } catch (error) {
        console.error(error);
        showError(error); // ou setError(error.message)
      }
  }, [user?.mail]);


  // Appelle fetchMembrePersonnel dès que l'email de l'utilisateur est disponible
  useEffect(() => {
    if (user?.mail) {
      fetchMembrePersonnel();
    } else {
      setMembrePersonnel({});
      setExistMembre("");
    }
  }, [user, fetchMembrePersonnel]);

  // Fonction pour associer un chemin de route à son composant React
  function getRouteElement(
    // path,
    route,
    routesConfig,
    user,
    checkGroupRecMensuelle,
    checkGroupAdmin,
    checkGroupSeancePlacement,
    checkGroupConsultationAchatEI
  ) {

    const { path, enabled } = route;

    switch (path) {
      case "/formulaire":
        return (
          <Formulaire
            user={user}
            checkGroupRecMensuelle={checkGroupRecMensuelle}
            checkGroupAdmin={checkGroupAdmin}
            enabled={enabled}
            // enabled={routesConfig.find(r => r.label === "Réclamation mensuelle de dépenses")?.enabled}
          />
        );
      case "/formulaireAdresse":
        return <FormulaireAdresse user={user} />;
      case "/formulaireNom":
        return <FormulaireNom user={user} />;
      case "/formulaireInfoBancaires":
        return <FormulaireInfoBancaires user={user} />;
      case "/formulaireReseauInfo":
        return <FormulaireReseauInfo user={user} />;
      case "/achatEquipInfo":
        return <FormAchatEI user={user} enabled={enabled} checkGroupAdmin={checkGroupAdmin} checkGroupConsultationAchatEI={checkGroupConsultationAchatEI} />
      case "/EntenteTravailHybrid":
        return  <EntenteTravailHybrid user={user} enabled={enabled}/>
      case "/antecedentsCriminels":
        return <AntecedentCriminel user={user} enabled={enabled}/>;
      case "/Acceshorspays":
        return <AccesHorsPays user={user} enabled={enabled} checkGroupAdmin={checkGroupAdmin}/>;
      case "/demandeProjet":
        return <DemandeProjet user={user} enabled={enabled}/>;
      case "/formAefo":
        // ✅ Render FormAefo only if existMembre === "exist dans AEFO"
        return (existMembre === "exist dans AEFO" || checkGroupAdmin || checkGroupSeancePlacement)

          ? <FormAefo
            user={user}
            membrePersonnel={membrePersonnel}
            checkGroupAdmin={checkGroupAdmin}
            checkGroupSeancePlacement={checkGroupSeancePlacement}
            routesConfig={routesConfig.filter(r => r.label === "Form AEFO")}
          enabled={enabled}
            // routesConfig={routesConfig.filter(route => route.label === "Form AEFO")}
            // enabled={routesConfig.find(r => r.label === "Form AEFO")?.enabled}
          />
          : null;
      case "/formFeeso":
        return (existMembre === "exist dans FEESO" || checkGroupAdmin || checkGroupSeancePlacement)

          ? <FormFeeso
            user={user}
            membrePersonnel={membrePersonnel}
            checkGroupAdmin={checkGroupAdmin}
            checkGroupSeancePlacement={checkGroupSeancePlacement}
            routesConfig={routesConfig.filter(r => r.label === "Form FEESO")}
          enabled={enabled}
            // routesConfig={routesConfig.filter(route => route.label === "Form FEESO")}
            // enabled={routesConfig.find(r => r.label === "Form FEESO")?.enabled}
          />
          : null;
      case "/formScfp":
        return (existMembre === "exist dans SCFP" || checkGroupAdmin || checkGroupSeancePlacement)

          ? <FormScfp
            user={user}
            membrePersonnel={membrePersonnel}
            checkGroupAdmin={checkGroupAdmin}
            checkGroupSeancePlacement={checkGroupSeancePlacement}
            routesConfig={routesConfig.filter(r => r.label === "Form SCFP")}
          enabled={enabled}
            // routesConfig={routesConfig.filter(route => route.label === "Form SCFP")}
            // enabled={routesConfig.find(r => r.label === "Form SCFP")?.enabled}
          />
          : null;

      default:
        return null;
    }
  }

  // **************************************************************** //
  // Affichage d'un loader pendant l'authentification
  if (authLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-75 z-50">
        <div className="w-12 h-12 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
        <p className="ml-3 text-gray-700 font-medium">Chargement en cours...</p>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <ScrollToTop />
       {/* Toastify GLOBAL */}
    {/* <ToastContainer
      position="top-center"
      autoClose={3000}
      hideProgressBar={false}
      closeOnClick
      pauseOnHover
      draggable
    /> */}
      <Routes>
        <Route
          path="/"
          element={
            <Header
              isSignedIn={isSignedIn}
              user={user}
              checkGroupAdresse={checkGroupAdresse}
              checkGroupAdmin={checkGroupAdmin}
              checkGroupNom={checkGroupNom}
              checkGroupReseauInfo={checkGroupReseauInfo}
              checkGroupInfoBancaire={checkGroupInfoBancaire}
              checkGroupAntCriminels={checkGroupAntCriminels}
              checkGroupRecMensuelle={checkGroupRecMensuelle}
              checkGroupSeancePlacement={checkGroupSeancePlacement}
              checkGroupTeletravail={checkGroupTeletravail}
              checkGroupConsultationAchatEI={checkGroupConsultationAchatEI}
              routesConfig={routesConfig}
              existMembre={existMembre}
            />
          }
        >
          <Route
            index
            element={
              <Home
                isSignedIn={isSignedIn}
                user={user}
              />
            }
          />

          {isSignedIn ? (
            <>
              {routesConfig.map((r) => {
                // For FormAefo, we always render the route
                if (r.label === "Form SCFP" || r.label === "Form FEESO" 
                  || r.label === "Form AEFO" || r.label === "Réclamation mensuelle de dépenses" 
                  || r.label === "Achat équipement informatique" || r.label === "Antécédents criminels"
                  || r.label === "Accès hors pays" || r.label === "Demande de projet" || r.label==="Entente de travail en mode hybride")
                {
                  return <Route key={r.path} path={r.path} element={r.element()} />;
                }

                // For other routes, filter by enabled
                if (!r.enabled) return null;

                return <Route key={r.path} path={r.path} element={r.element()} />;
              })}

              <Route path="/demande" element={<Demande user={user} />} />
              <Route
                path="/participationFormation"
                element={<ParticipationFormation user={user} />}
              />

              {(checkGroupAdmin || checkGroupRecMensuelle || checkGroupSeancePlacement ||checkGroupAntCriminels || checkGroupTeletravail) && (
                <Route
                  path="/gestionDonnéesFinancières"
                  element={
                    <GestionDonneeFinance
                      user={user}
                      checkGroupRecMensuelle={checkGroupRecMensuelle}
                      checkGroupAdmin={checkGroupAdmin}
                      checkGroupNom={checkGroupNom}
                      checkGroupAdresse={checkGroupAdresse}
                      checkGroupInfoBancaire={checkGroupInfoBancaire}
                      checkGroupAntCriminels={checkGroupAntCriminels}
                      checkGroupSeancePlacement={checkGroupSeancePlacement}
                      checkGroupTeletravail={checkGroupTeletravail}
                      routesConfig={routesConfig.filter(route => ["Form AEFO", "Form FEESO", "Form SCFP"].includes(route.label))}
                      setRoutesConfig={setRoutesConfig}
                    />
                  }
                />
              )}
              {(checkGroupAdmin || checkGroupRecMensuelle ||checkGroupInfoBancaire || checkGroupAntCriminels) && (
                <Route
                  path="/Archives"
                  element={
                    <ArchivesForms
                      user={user}
                      checkGroupRecMensuelle={checkGroupRecMensuelle}
                      checkGroupAdmin={checkGroupAdmin}
                      checkGroupInfoBancaire={checkGroupInfoBancaire}
                      checkGroupAntCriminels={checkGroupAntCriminels}
                    />
                  }
                />
              )}

              <Route
                path="/taches"
                element={
                  <Taches
                    user={user}
                    checkGroupNom={checkGroupNom}
                    checkGroupAdresse={checkGroupAdresse}
                    checkGroupAdmin={checkGroupAdmin}
                    checkGroupReseauInfo={checkGroupReseauInfo}
                    checkGroupInfoBancaire={checkGroupInfoBancaire}
                    checkGroupAntCriminels={checkGroupAntCriminels}
                    checkGroupRecMensuelle={checkGroupRecMensuelle}
                    checkGroupSeancePlacement={checkGroupSeancePlacement}
                    checkGroupTeletravail={checkGroupTeletravail}
                    checkGroupConsultationAchatEI={checkGroupConsultationAchatEI}
                    loading={loading}
                  />
                }
              />
              {/* )} */}

              {checkGroupAdmin && (
                <Route
                  path="/groupRoles"
                  element={
                    <GroupRoles
                      user={user}
                      routesConfig={routesConfig}
                      setRoutesConfig={setRoutesConfig}
                    />
                  }
                />
              )}
            </>
          ) : (
            <Route
              path="*"
              element={
                authLoading ? (
                  <div>Chargement en cours...</div>
                ) : (
                  <AuthenticationRedirect />
                )
              }
            />
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;





